// PRE RMA CSB — Calcul du RMA (Parties 1 a 6) et du Tableau de suivi.
// Reproduit exactement les formules de la feuille RMA (Phase1 §4/§5).
//
// [CORRECTION VALIDEE #2] Partie 1, ligne "Cas de fièvre", tranche "2-11mois M":
//   la formule etait absente dans le fichier Excel original (oubli). Ici la
//   colonne est calculee de facon symetrique a toutes les autres, comme demande.
// [CORRECTION VALIDEE #3] Tableau de suivi "Admis"/"Guérie": interrogent
//   desormais admissionCrenas (equivalent colonne M) au lieu de typeTraumatisme
//   (equivalent colonne J, qui etait la mauvaise reference dans le fichier).

import { DIAGNOSTICS_REF, DIAGNOSTICS_PALUDISME, RULES } from './constants.ts';
import {
  trancheMorbidite,
  trancheTraumatisme,
  tranchePalu,
  trancheNutrition,
  trancheCsaj,
  TRANCHES_MORBIDITE,
  ageJoursValide,
} from './tranches.ts';
import type { Genre, TdrResultat } from './constants.ts';

export interface ConsultationRow {
  numeroRceBrut: string;
  casFievre: 'Oui' | 'Non' | null;
  tdrResultat: TdrResultat | null;
  ageJours: number | null;     // champ pivot deja calcule (voir age.ts)
  genre: Genre | null;
  diagnostics: string[];       // codes DIAGNOSTICS_REF
  typeTraumatisme: string | null;    // code traumatismes_ref, ou 'AUCUN'/null
  traitementPalu: string | null;     // libelle exact
  indicateurCrenas: string | null;   // libelle(s), separes par virgule
  admissionCrenas: string | null;    // libelle exact
}

const GENRES: Genre[] = ['M', 'F'];

function ligneUtile(row: ConsultationRow): boolean {
  return row.numeroRceBrut.trim() !== '';
}

// ---------------------------------------------------------------------
// NORMALISATION DES LIGNES — porte d'entrée unique de tous les calculs.
// Une ligne venue d'ailleurs (base SQL, file IndexedDB, fichier importé, test) peut avoir un champ absent, `undefined`, en
// minuscules, une chaîne à la place d'un nombre… Sans cette étape un seul champ inattendu faisait planter TOUT le RMA
// (ex. `diagnostics` absent) ou rangeait silencieusement le patient dans la dernière tranche d'âge (« 25 ans et plus »).
// Fonction PURE et IDEMPOTENTE : normaliser deux fois donne le même résultat.
// ---------------------------------------------------------------------
const texteOuNull = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const t = String(v).replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ').trim();
  return t === '' ? null : t;
};

export function normaliserLigne(row: Partial<Record<keyof ConsultationRow, unknown>> | null | undefined): ConsultationRow {
  const r = (row ?? {}) as Record<string, unknown>;
  const fievre = texteOuNull(r.casFievre)?.toLowerCase();
  const casFievre = r.casFievre === true || fievre === 'oui' || fievre === 'o' || fievre === 'true' ? 'Oui'
    : r.casFievre === false || fievre === 'non' || fievre === 'n' || fievre === 'false' ? 'Non' : null;
  const g = texteOuNull(r.genre)?.toUpperCase();
  const genre: Genre | null = g === 'M' || g === 'F' ? g : g === 'H' || g === 'MASCULIN' || g === 'HOMME' ? 'M' : g === 'FEMININ' || g === 'FÉMININ' || g === 'FEMME' ? 'F' : null;
  const tdr = texteOuNull(r.tdrResultat);
  const diagnostics = Array.isArray(r.diagnostics)
    ? [...new Set(r.diagnostics.map(texteOuNull).filter((d): d is string => d !== null))]
    : [];
  return {
    numeroRceBrut: texteOuNull(r.numeroRceBrut) ?? '',
    casFievre,
    tdrResultat: (tdr && /^(100|101|110|111)$/.test(tdr) ? tdr : null) as TdrResultat | null,
    ageJours: ageJoursValide(r.ageJours),
    genre,
    diagnostics,
    typeTraumatisme: texteOuNull(r.typeTraumatisme),
    traitementPalu: texteOuNull(r.traitementPalu),
    indicateurCrenas: texteOuNull(r.indicateurCrenas),
    admissionCrenas: texteOuNull(r.admissionCrenas),
  };
}

export function normaliserLignes(rows: ReadonlyArray<Partial<Record<keyof ConsultationRow, unknown>> | null | undefined> | null | undefined): ConsultationRow[] {
  return Array.isArray(rows) ? rows.map(normaliserLigne) : [];
}

// ---------------------------------------------------------------------
// CONTRÔLE DES LIGNES : rien ne disparaît en silence.
// Les règles du RMA (identiques au classeur Excel d'origine) ignorent une ligne sans N° RCE, et les Parties 1-2 ignorent une ligne
// sans âge ou sans genre. Ce contrôle COMPTE ces lignes, pour que l'écran, l'export Excel et le PDF puissent les signaler au lieu
// de produire un total « faux sans le dire ».
// ---------------------------------------------------------------------
export interface ControleLignes {
  total: number;
  /** Lignes avec N° RCE (celles qui comptent dans les Parties 1, 2 et 4). */
  utiles: number;
  sansNumeroRce: number;
  /** Lignes avec N° RCE mais sans âge exploitable : absentes des Parties 1, 2, 4, 5, 6. */
  ageManquant: number;
  /** Lignes avec N° RCE mais sans genre : absentes des Parties 1 et 2. */
  genreManquant: number;
  /** Lignes avec N° RCE absentes des Parties 1-2 (âge OU genre manquant). */
  exclues: number;
  /** Cas de fièvre sans âge exploitable : absents de la Partie 3 (paludisme, 5 tranches). */
  fievreSansAge: number;
  /** Lignes SANS N° RCE portant pourtant un diagnostic ou un traumatisme : non comptées. */
  diagnosticsSansNumeroRce: number;
  /** true quand aucune ligne n'est écartée du calcul. */
  complet: boolean;
}

export function controlerLignes(rows: ReadonlyArray<Partial<Record<keyof ConsultationRow, unknown>> | null | undefined> | null | undefined): ControleLignes {
  const L = normaliserLignes(rows);
  let utiles = 0, ageManquant = 0, genreManquant = 0, exclues = 0, fievreSansAge = 0, diagnosticsSansNumeroRce = 0;
  for (const r of L) {
    if (r.casFievre === 'Oui' && r.ageJours === null) fievreSansAge++;
    if (!ligneUtile(r)) {
      if (r.diagnostics.length > 0 || (r.typeTraumatisme !== null && r.typeTraumatisme !== 'AUCUN')) diagnosticsSansNumeroRce++;
      continue;
    }
    utiles++;
    if (r.ageJours === null) ageManquant++;
    if (r.genre === null) genreManquant++;
    if (r.ageJours === null || r.genre === null) exclues++;
  }
  return {
    total: L.length, utiles, sansNumeroRce: L.length - utiles, ageManquant, genreManquant, exclues, fievreSansAge, diagnosticsSansNumeroRce,
    complet: exclues === 0 && fievreSansAge === 0 && diagnosticsSansNumeroRce === 0,
  };
}

/** Phrases lisibles décrivant ce qui n'est PAS compté (vide si tout est compté). Sert à l'écran, à l'Excel et au PDF. */
export function avertissementsControle(c: ControleLignes): string[] {
  const out: string[] = [];
  if (c.ageManquant) out.push(`${c.ageManquant} ligne(s) avec N° RCE sans âge exploitable : non comptée(s) dans les parties 1, 2, 4, 5 et 6.`);
  if (c.genreManquant) out.push(`${c.genreManquant} ligne(s) avec N° RCE sans genre : non comptée(s) dans les parties 1 et 2.`);
  if (c.fievreSansAge) out.push(`${c.fievreSansAge} cas de fièvre sans âge exploitable : non compté(s) dans le paludisme (partie 3).`);
  if (c.diagnosticsSansNumeroRce) out.push(`${c.diagnosticsSansNumeroRce} ligne(s) avec diagnostic ou traumatisme mais sans N° RCE : non comptée(s).`);
  return out;
}

// ---------------------------------------------------------------------
// PARTIE 1 : Diagnostic / Symptomes
// ---------------------------------------------------------------------
export interface Partie1Ligne {
  code: string;        // code stable (les libellés suivent la liste officielle du RMA et peuvent évoluer)
  diagnostic: string;
  parTranche: Record<string, number>; // cle "0-28j M", etc.
  total: number;
  /** Ancienne ligne regroupée (voir rules.config.ts) : affichée seulement s'il reste des cas déjà saisis. */
  legacy?: boolean;
}
export interface Partie1CasFievreLigne {
  parTranche: Record<string, number>;
  total: number;
}

export function calculerPartie1(brutes: ConsultationRow[]): {
  lignes: Partie1Ligne[];
  totalCE: Record<string, number>;
  casFievre: Partie1CasFievreLigne;
} {
  const rows = normaliserLignes(brutes);
  const toutesTranches: string[] = [];
  for (const t of TRANCHES_MORBIDITE) for (const g of GENRES) toutesTranches.push(`${t} ${g}`);

  const lignes: Partie1Ligne[] = DIAGNOSTICS_REF.map((d) => {
    const parTranche: Record<string, number> = Object.fromEntries(toutesTranches.map((t) => [t, 0]));
    for (const row of rows) {
      if (!ligneUtile(row) || row.ageJours === null || !row.genre) continue;
      if (!row.diagnostics.includes(d.code)) continue;
      const tranche = trancheMorbidite(row.ageJours, row.genre);
      parTranche[tranche] = (parTranche[tranche] ?? 0) + 1;
    }
    const total = Object.values(parTranche).reduce((a, b) => a + b, 0);
    return { code: d.code, diagnostic: d.libelle, parTranche, total, legacy: !!d.legacy };
  }).filter((l) => !l.legacy || l.total > 0); // ancienne ligne regroupée : visible seulement s'il reste des cas déjà saisis

  const totalCE: Record<string, number> = Object.fromEntries(toutesTranches.map((t) => [t, 0]));
  for (const ligne of lignes) {
    for (const t of toutesTranches) totalCE[t] += ligne.parTranche[t];
  }

  // [CORRECTION VALIDEE #2] toutes les tranches sont calculees, y compris "2-11mois M".
  const casFievreParTranche: Record<string, number> = Object.fromEntries(toutesTranches.map((t) => [t, 0]));
  for (const row of rows) {
    if (!ligneUtile(row) || row.casFievre !== 'Oui' || row.ageJours === null || !row.genre) continue;
    const tranche = trancheMorbidite(row.ageJours, row.genre);
    casFievreParTranche[tranche] = (casFievreParTranche[tranche] ?? 0) + 1;
  }
  const casFievreTotal = Object.values(casFievreParTranche).reduce((a, b) => a + b, 0);

  return { lignes, totalCE, casFievre: { parTranche: casFievreParTranche, total: casFievreTotal } };
}

// ---------------------------------------------------------------------
// PARTIE 2 : Traumatismes et violences
// ---------------------------------------------------------------------
export interface Partie2Ligne {
  categorie: string;
  parTranche: Record<string, number>;
  totalM: number;
  totalF: number;
}
const TRAUMA_CATEGORIES = RULES.traumatismes.categories;

export function calculerPartie2(brutes: ConsultationRow[]): Partie2Ligne[] {
  const rows = normaliserLignes(brutes);
  return TRAUMA_CATEGORIES.map((cat) => {
    const parTranche: Record<string, number> = {};
    let totalM = 0;
    let totalF = 0;
    for (const row of rows) {
      if (!ligneUtile(row) || row.typeTraumatisme !== cat || row.ageJours === null || !row.genre) continue;
      const tranche = trancheTraumatisme(row.ageJours, row.genre);
      parTranche[tranche] = (parTranche[tranche] ?? 0) + 1;
      if (row.genre === 'M') totalM += 1;
      else totalF += 1;
    }
    return { categorie: cat, parTranche, totalM, totalF };
  });
}

// ---------------------------------------------------------------------
// PARTIE 3 : Prise en charge du paludisme
// ---------------------------------------------------------------------
export interface Partie3Ligne {
  tranche: string;
  casFievre: number;
  tdrEffectue: number;
  positif101: number;
  positif110: number;
  positif111: number;
  hommes: number;
  femmes: number;
  traitesACT: number;
  traitesArtesunate: number;
}
const TRANCHES_PALU = RULES.tranches.palu.map((b) => b.label);

export function calculerPartie3(brutes: ConsultationRow[]): Partie3Ligne[] {
  const rows = normaliserLignes(brutes);
  return TRANCHES_PALU.map((tranche) => {
    const lignesTranche = rows.filter((r) => r.ageJours !== null && tranchePalu(r.ageJours) === tranche);
    const casFievre = lignesTranche.filter((r) => r.casFievre === 'Oui').length;
    const tdrEffectue = lignesTranche.filter((r) => r.casFievre === 'Oui' && r.tdrResultat).length;
    const positif101 = lignesTranche.filter((r) => r.tdrResultat === '101').length;
    const positif110 = lignesTranche.filter((r) => r.tdrResultat === '110').length;
    const positif111 = lignesTranche.filter((r) => r.tdrResultat === '111').length;
    const positifs = RULES.tdr.positifs;
    const hommes = lignesTranche.filter((r) => r.genre === 'M' && positifs.includes(r.tdrResultat as any)).length;
    const femmes = lignesTranche.filter((r) => r.genre === 'F' && positifs.includes(r.tdrResultat as any)).length;
    const traitesACT = lignesTranche.filter((r) => r.traitementPalu?.includes(RULES.traitement.motCleAct)).length;
    const traitesArtesunate = lignesTranche.filter((r) => r.traitementPalu?.includes(RULES.traitement.motCleArtesunate)).length;
    return { tranche, casFievre, tdrEffectue, positif101, positif110, positif111, hommes, femmes, traitesACT, traitesArtesunate };
  });
}

/** Ligne « Total » de la Partie 3 : somme de chaque colonne sur les 5 tranches (fonction pure, utilisée par l'écran RMA). */
export type Partie3Total = Omit<Partie3Ligne, 'tranche'>;
export function totalPartie3(lignes: readonly Partie3Ligne[]): Partie3Total {
  const somme = (k: keyof Partie3Total) => lignes.reduce((a, l) => a + (l[k] || 0), 0);
  return {
    casFievre: somme('casFievre'), tdrEffectue: somme('tdrEffectue'),
    positif101: somme('positif101'), positif110: somme('positif110'), positif111: somme('positif111'),
    hommes: somme('hommes'), femmes: somme('femmes'),
    traitesACT: somme('traitesACT'), traitesArtesunate: somme('traitesArtesunate'),
  };
}

// ---------------------------------------------------------------------
// PARTIE 4 : Programme Nutrition — enfants depistes
// ---------------------------------------------------------------------
export interface Partie4 {
  poids: Record<string, number>;
  taille: Record<string, number>;
  pb: Record<string, number>;           // toujours 0 pour "0-5 mois"
  rechercheOedeme: Record<string, number>; // toujours 0 pour "0-5 mois"
}
const TRANCHES_NUTRITION = RULES.tranches.nutrition.bornes.map((b) => b.label);

export function calculerPartie4(brutes: ConsultationRow[]): Partie4 {
  const rows = normaliserLignes(brutes);
  const base = (): Record<string, number> => Object.fromEntries(TRANCHES_NUTRITION.map((t) => [t, 0]));
  const poids = base();
  const taille = base();
  const pb = base();
  const rechercheOedeme = base();

  for (const row of rows) {
    if (!ligneUtile(row) || row.ageJours === null) continue;
    const tranche = trancheNutrition(row.ageJours);
    if (!tranche) continue;
    poids[tranche] += 1;
    taille[tranche] += 1;
    if (tranche !== TRANCHES_NUTRITION[0]) {
      if (row.indicateurCrenas?.includes(RULES.crenas.motClePb)) pb[tranche] += 1;
      if (RULES.crenas.motClesOedeme.some((mot) => row.indicateurCrenas?.includes(mot))) {
        rechercheOedeme[tranche] += 1;
      }
    }
  }
  return { poids, taille, pb, rechercheOedeme };
}

// ---------------------------------------------------------------------
// PARTIE 5 : Programme Nutrition — indicateurs et admissions CRENAS
// ---------------------------------------------------------------------
export interface Partie5Ligne {
  tranche: string;
  mamPT: number;
  mamPB: number;
  masPT: number;
  masPB: number;
  admisNaM: number;
  admisNaF: number;
  admisRechuteM: number;
  admisRechuteF: number;
  gueri: number;
  sortieAutre: number;
}

export function calculerPartie5(brutes: ConsultationRow[]): Partie5Ligne[] {
  const rows = normaliserLignes(brutes);
  return TRANCHES_NUTRITION.map((tranche) => {
    const lignesTranche = rows.filter((r) => r.ageJours !== null && trancheNutrition(r.ageJours) === tranche);
    const count = (pred: (r: ConsultationRow) => boolean) => lignesTranche.filter(pred).length;
    return {
      tranche,
      mamPT: count((r) => !!r.indicateurCrenas?.includes(RULES.crenas.libelles.mamPT)),
      mamPB: count((r) => !!r.indicateurCrenas?.includes(RULES.crenas.libelles.mamPB)),
      masPT: count((r) => !!r.indicateurCrenas?.includes(RULES.crenas.libelles.masPT)),
      masPB: count((r) => !!r.indicateurCrenas?.includes(RULES.crenas.libelles.masPB)),
      admisNaM: count((r) => r.admissionCrenas === RULES.crenas.admissionsParGenre.M[0]),
      admisNaF: count((r) => r.admissionCrenas === RULES.crenas.admissionsParGenre.F[0]),
      admisRechuteM: count((r) => r.admissionCrenas === RULES.crenas.admissionsParGenre.M[1]),
      admisRechuteF: count((r) => r.admissionCrenas === RULES.crenas.admissionsParGenre.F[1]),
      gueri: count((r) => r.admissionCrenas === RULES.crenas.libelleGueri),
      sortieAutre: count((r) => r.admissionCrenas === RULES.crenas.libelleSortieAutre),
    };
  });
}

// ---------------------------------------------------------------------
// PARTIE 6 : CSAJ
// ---------------------------------------------------------------------
export interface Partie6 {
  ist: Record<'10-14' | '15-19' | '20-24', number>;
  ce: Record<'10-14' | '15-19' | '20-24', number>;
}

export function calculerPartie6(brutes: ConsultationRow[]): Partie6 {
  const rows = normaliserLignes(brutes);
  const ist: Partie6['ist'] = { '10-14': 0, '15-19': 0, '20-24': 0 };
  const ce: Partie6['ce'] = { '10-14': 0, '15-19': 0, '20-24': 0 };
  for (const row of rows) {
    if (row.ageJours === null) continue;
    const tranche = trancheCsaj(row.ageJours);
    if (!tranche) continue;
    ce[tranche] += 1;
    // Ulceration + Ecoulement chez le meme patient = 1 seule fois (point 24)
    const aIst = row.diagnostics.some((d) => (RULES.csaj.diagnosticsIst as readonly string[]).includes(d));
    if (aIst) ist[tranche] += 1;
  }
  return { ist, ce };
}

// ---------------------------------------------------------------------
// TABLEAU DE SUIVI
// ---------------------------------------------------------------------
export interface TableauSuivi {
  totalCE: number;
  ceMoins5ans: number;
  ce5ansEtPlus: number;
  totalFievre: number;
  fievreMoins5ans: number;
  fievre5ansEtPlus: number;
  totalPaludisme: number;
  paludismeMoins5ans: number;
  paludisme5ansEtPlus: number;
  // Complément Phase 8 (non-régression) : colonnes du Tableau de suivi
  // réel de Pre_RMA_V12.xlsx, absentes de la première implémentation
  // (Phase 3) — aucune règle métier n'est modifiée ici, ces valeurs
  // dérivent des mêmes lignes de Partie 1 déjà calculées et déjà testées.
  totalPaludismeSimple: number;
  paludismeSimpleMoins5ans: number;
  paludismeSimple5ansEtPlus: number;
  totalPaludismeGrave: number;
  paludismeGraveMoins5ans: number;
  paludismeGrave5ansEtPlus: number;
  infectionRespiratoire: number; // diagnostic "Infection Respiratoire Aigue"
  pneumonie: number;             // diagnostic "Pneumonie"
  diarrheeMoins5ans: number;     // diagnostic "Diarrhée sans DH2O"
  diarrhee5ansEtPlus: number;
  dysenterie: number;
  sdGrippal: number;
  mas: number;
  mam: number;
  admis: number;   // [CORRECTION VALIDEE #3]
  guerie: number;  // [CORRECTION VALIDEE #3]
}

const TRAUMA_CODES = RULES.traumatismes.categories;

export function calculerTableauSuivi(brutes: ConsultationRow[], partie1: ReturnType<typeof calculerPartie1>): TableauSuivi {
  const rows = normaliserLignes(brutes);
  const moins5 = RULES.tableauSuivi.tranchesMoins5ans;
  const plus5 = RULES.tableauSuivi.tranches5ansEtPlus;

  // Point Phase1 §5 : A2 "TOTAL CE" = RMA!R40 (total Partie1, diagnostics)
  // + la grille Partie 2 (traumatismes/violences). Idem pour la repartition
  // <5ans / >=5ans (B2/C2), qui additionne aussi la part traumatismes.
  const diagTotal = Object.values(partie1.totalCE).reduce((a, b) => a + b, 0);
  let traumaTotal = 0;
  let traumaMoins5 = 0;
  let traumaPlus5 = 0;
  for (const row of rows) {
    if (!ligneUtile(row) || !row.typeTraumatisme || !(TRAUMA_CODES as readonly string[]).includes(row.typeTraumatisme)) continue;
    if (row.ageJours === null) continue;
    traumaTotal += 1;
    if (row.ageJours < RULES.age.seuils.cinqAns) traumaMoins5 += 1;
    else traumaPlus5 += 1;
  }
  const totalCE = diagTotal + traumaTotal;
  const sumTranches = (obj: Record<string, number>, prefixes: readonly string[]) =>
    Object.entries(obj)
      .filter(([k]) => prefixes.some((p) => k.startsWith(p + ' ')))
      .reduce((a, [, v]) => a + v, 0);

  const paludismeCodes = DIAGNOSTICS_PALUDISME;
  const paludismeLignes = partie1.lignes.filter((l) =>
    (paludismeCodes as readonly string[]).includes(l.code)
  );
  const paludismeTotal = paludismeLignes.reduce((a, l) => a + l.total, 0);
  const paludismeMoins5 = paludismeLignes.reduce((a, l) => a + sumTranches(l.parTranche, moins5), 0);
  const paludisme5Plus = paludismeLignes.reduce((a, l) => a + sumTranches(l.parTranche, plus5), 0);

  // [CORRECTION VALIDEE #3] "Admis" / "Guérie" lisent admissionCrenas, pas typeTraumatisme.
  const admis = rows.filter((r) => ligneUtile(r) && r.admissionCrenas?.startsWith(RULES.crenas.prefixeAdmis)).length;
  const guerie = rows.filter((r) => ligneUtile(r) && r.admissionCrenas === RULES.crenas.libelleGueri).length;

  // Complément Phase 8 : indicateurs supplémentaires du Tableau de suivi réel,
  // tous dérivés des lignes de Partie 1 déjà calculées (aucune nouvelle règle).
  const ligneParCode = (code: string) => partie1.lignes.find((l) => l.code === code);
  const totalDiagnostic = (code: string) => ligneParCode(code)?.total ?? 0;
  const splitDiagnostic = (code: string) => {
    const ligne = ligneParCode(code);
    return {
      moins5: ligne ? sumTranches(ligne.parTranche, moins5) : 0,
      plus5: ligne ? sumTranches(ligne.parTranche, plus5) : 0,
    };
  };
  const paluSimple = splitDiagnostic('PALUDISME_SIMPLE');
  const paluGrave = splitDiagnostic('PALUDISME_GRAVE');
  const diarrhee = splitDiagnostic('DIARRHEE_SANS_DH2O');

  return {
    totalCE,
    ceMoins5ans: sumTranches(partie1.totalCE, moins5) + traumaMoins5,
    ce5ansEtPlus: sumTranches(partie1.totalCE, plus5) + traumaPlus5,
    totalFievre: partie1.casFievre.total,
    fievreMoins5ans: sumTranches(partie1.casFievre.parTranche, moins5),
    fievre5ansEtPlus: sumTranches(partie1.casFievre.parTranche, plus5),
    totalPaludisme: paludismeTotal,
    paludismeMoins5ans: paludismeMoins5,
    paludisme5ansEtPlus: paludisme5Plus,
    totalPaludismeSimple: totalDiagnostic('PALUDISME_SIMPLE'),
    paludismeSimpleMoins5ans: paluSimple.moins5,
    paludismeSimple5ansEtPlus: paluSimple.plus5,
    totalPaludismeGrave: totalDiagnostic('PALUDISME_GRAVE'),
    paludismeGraveMoins5ans: paluGrave.moins5,
    paludismeGrave5ansEtPlus: paluGrave.plus5,
    infectionRespiratoire: totalDiagnostic('IRA'),
    pneumonie: totalDiagnostic('PNEUMONIE'),
    diarrheeMoins5ans: diarrhee.moins5,
    diarrhee5ansEtPlus: diarrhee.plus5,
    dysenterie: totalDiagnostic('DYSENTERIE'),
    sdGrippal: totalDiagnostic('SD_GRIPPAL'),
    mas: totalDiagnostic('MAS'),
    mam: totalDiagnostic('MAM'),
    admis,
    guerie,
  };
}

// ---------------------------------------------------------------------
// CALCUL COMPLET EN UN SEUL APPEL (serveur, export, écran, hors connexion : mêmes chiffres partout)
// ---------------------------------------------------------------------
export interface RmaCalcule {
  partie1: ReturnType<typeof calculerPartie1>;
  partie2: Partie2Ligne[];
  partie3: Partie3Ligne[];
  partie4: Partie4;
  partie5: Partie5Ligne[];
  partie6: Partie6;
  tableauSuivi: TableauSuivi;
  controle: ControleLignes;
}

export function calculerRmaDepuisLignes(brutes: ConsultationRow[]): RmaCalcule {
  const rows = normaliserLignes(brutes); // normalisé UNE fois ; les calculerPartieN re-normalisent à coût négligeable (idempotent)
  const partie1 = calculerPartie1(rows);
  return {
    partie1,
    partie2: calculerPartie2(rows),
    partie3: calculerPartie3(rows),
    partie4: calculerPartie4(rows),
    partie5: calculerPartie5(rows),
    partie6: calculerPartie6(rows),
    tableauSuivi: calculerTableauSuivi(rows, partie1),
    controle: controlerLignes(rows),
  };
}
